import axios from 'axios'
import Button from '@material-ui/core/Button'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { settingsHost, gstSettingsHost } from 'utils/Hosts'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { clearTMDBCache } from 'components/Add/helpers'
import AppBar from '@material-ui/core/AppBar'
import SwipeableViews from 'react-swipeable-views'
import CircularProgress from '@material-ui/core/CircularProgress'
import { StyledDialog } from 'style/CustomMaterialUiStyles'
import useOnStandaloneAppOutsideClick from 'utils/useOnStandaloneAppOutsideClick'
import { normalizeTrayOpenURL } from 'utils/TrayURL'

import { SettingsHeader, FooterSection, Content, StyledTabs, StyledTab } from './style'
import defaultSettings from './defaultSettings'
import { a11yProps, TabPanel } from './tabComponents'
import PrimarySettingsComponent from './PrimarySettingsComponent'
import SecondarySettingsComponent from './SecondarySettingsComponent'
import MobileAppSettings from './MobileAppSettings'
import TMDBSettings from './TMDBSettings'
import GStreamerSettings from './GStreamerSettings'
import SecuritySettings from './SecuritySettings'
import SecurityAuditSettings from './SecurityAuditSettings'
import TraySettings from './TraySettings'

export default function SettingsDialog({ handleClose }) {
  const { t } = useTranslation()
  const fullScreen = useMediaQuery('@media (max-width:930px)')
  const { direction } = useTheme()

  const [settings, setSettings] = useState()
  const [selectedTab, setSelectedTab] = useState(0)
  const [cacheSize, setCacheSize] = useState(32)
  const [cachePercentage, setCachePercentage] = useState(40)
  const [preloadCachePercentage, setPreloadCachePercentage] = useState(0)
  const [preloadCacheSize, setPreloadCacheSize] = useState(50)
  const [isVlcUsed, setIsVlcUsed] = useState(JSON.parse(localStorage.getItem('isVlcUsed')) ?? false)
  const [isInfuseUsed, setIsInfuseUsed] = useState(JSON.parse(localStorage.getItem('isInfuseUsed')) ?? false)
  const [isSenPlayerUsed, setIsSenPlayerUsed] = useState(JSON.parse(localStorage.getItem('isSenPlayerUsed')) ?? false)
  const [isIinaUsed, setIsIinaUsed] = useState(JSON.parse(localStorage.getItem('isIinaUsed')) ?? false)
  const [gstAvailable, setGstAvailable] = useState(false)

  const tabMain = 0
  const tabAdditional = 1
  const tabApp = 2
  const tabSecurity = 3
  const tabSecurityAudit = 4
  const tabGStreamer = 5
  const maxTab = gstAvailable ? tabGStreamer : tabSecurityAudit

  useEffect(() => {
    fetch(gstSettingsHost())
      .then(response => (response.ok ? response.json() : { built_in: false }))
      .then(data => setGstAvailable(Boolean(data.built_in)))
      .catch(() => setGstAvailable(false))
  }, [])

  useEffect(() => {
    axios.post(settingsHost(), { action: 'get' }).then(({ data }) => {
      setSettings({ ...data, CacheSize: data.CacheSize / (1024 * 1024) })
    })
  }, [])

  const ref = useOnStandaloneAppOutsideClick(handleClose)

  const handleSave = () => {
    const sets = JSON.parse(JSON.stringify(settings))
    const normalizedTrayOpenURL = normalizeTrayOpenURL(sets.TrayOpenURL)
    if (!normalizedTrayOpenURL) {
      setSelectedTab(tabApp)
      return
    }
    sets.TrayOpenURL = normalizedTrayOpenURL
    sets.CacheSize = cacheSize * 1024 * 1024
    sets.ReaderReadAHead = cachePercentage
    sets.PreloadCache = preloadCachePercentage
    sets.PreloadCacheSize = preloadCacheSize
    axios.post(settingsHost(), { action: 'set', sets })
    // Clear TMDB cache so fresh settings are fetched on next poster search
    clearTMDBCache()
    localStorage.setItem('isVlcUsed', isVlcUsed)
    localStorage.setItem('isInfuseUsed', isInfuseUsed)
    localStorage.setItem('isSenPlayerUsed', isSenPlayerUsed)
    localStorage.setItem('isIinaUsed', isIinaUsed)
    handleClose()
  }

  const inputForm = ({ target: { type, value, checked, id } }) => {
    const sets = JSON.parse(JSON.stringify(settings))

    if (type === 'number' || type === 'select-one') {
      sets[id] = Number(value)
    } else if (type === 'checkbox') {
      if (
        id === 'DisableTCP' ||
        id === 'DisableUTP' ||
        id === 'DisableUPNP' ||
        id === 'DisableDHT' ||
        id === 'DisablePEX' ||
        id === 'DisableUpload'
      )
        sets[id] = Boolean(!checked)
      else sets[id] = Boolean(checked)
    } else if (type === 'url' || type === 'text') {
      sets[id] = value
    } else if (!type && value !== undefined) {
      // Fallback for custom handlers that don't provide type
      sets[id] = value
    }
    setSettings(sets)
  }

  useEffect(() => {
    if (selectedTab > maxTab) {
      setSelectedTab(0)
    }
  }, [gstAvailable, selectedTab, maxTab])

  const { CacheSize, ReaderReadAHead, PreloadCache, PreloadCacheSize } = settings || {}

  useEffect(() => {
    if (isNaN(CacheSize) || isNaN(ReaderReadAHead) || isNaN(PreloadCache) || isNaN(PreloadCacheSize)) return

    setCacheSize(CacheSize)
    setCachePercentage(ReaderReadAHead)
    setPreloadCachePercentage(PreloadCache)
    setPreloadCacheSize(PreloadCacheSize)
  }, [CacheSize, ReaderReadAHead, PreloadCache, PreloadCacheSize])

  const updateSettings = newProps => setSettings({ ...settings, ...newProps })
  const isTrayOpenURLValid = !settings || Boolean(normalizeTrayOpenURL(settings.TrayOpenURL))
  const handleChange = (_, newValue) => setSelectedTab(newValue)
  const handleChangeIndex = index => setSelectedTab(index)

  return (
    <StyledDialog open onClose={handleClose} fullScreen={fullScreen} fullWidth maxWidth='md' ref={ref}>
      <SettingsHeader>
        <div>{t('SettingsDialog.Settings')}</div>
      </SettingsHeader>

      <AppBar position='static' color='default'>
        <StyledTabs
          value={selectedTab}
          onChange={handleChange}
          indicatorColor='secondary'
          textColor='secondary'
          variant='scrollable'
          scrollButtons='auto'
        >
          <StyledTab label={t('SettingsDialog.Tabs.Main')} {...a11yProps(0)} />

          <StyledTab label={t('SettingsDialog.Tabs.Additional')} {...a11yProps(1)} />

          <StyledTab label={t('SettingsDialog.Tabs.App')} {...a11yProps(tabApp)} />

          <StyledTab label={t('Security.Tab')} {...a11yProps(tabSecurity)} />

          <StyledTab label={t('SecurityAudit.Tab')} {...a11yProps(tabSecurityAudit)} />

          {gstAvailable && <StyledTab label={t('GStreamer.Tab')} {...a11yProps(tabGStreamer)} />}
        </StyledTabs>
      </AppBar>

      <Content isLoading={!settings}>
        {settings ? (
          <>
            <SwipeableViews
              axis={direction === 'rtl' ? 'x-reverse' : 'x'}
              index={selectedTab}
              onChangeIndex={handleChangeIndex}
            >
              <TabPanel value={selectedTab} index={tabMain} dir={direction}>
                <PrimarySettingsComponent
                  settings={settings}
                  inputForm={inputForm}
                  cachePercentage={cachePercentage}
                  preloadCachePercentage={preloadCachePercentage}
                  preloadCacheSize={preloadCacheSize}
                  cacheSize={cacheSize}
                  setCacheSize={setCacheSize}
                  setCachePercentage={setCachePercentage}
                  setPreloadCachePercentage={setPreloadCachePercentage}
                  setPreloadCacheSize={setPreloadCacheSize}
                  updateSettings={updateSettings}
                />
              </TabPanel>

              <TabPanel value={selectedTab} index={tabAdditional} dir={direction}>
                <SecondarySettingsComponent settings={settings} inputForm={inputForm} updateSettings={updateSettings} />
              </TabPanel>

              <TabPanel value={selectedTab} index={tabApp} dir={direction}>
                <TraySettings settings={settings} updateSettings={updateSettings} />
                <TMDBSettings settings={settings} updateSettings={updateSettings} />
                <MobileAppSettings
                  isVlcUsed={isVlcUsed}
                  setIsVlcUsed={setIsVlcUsed}
                  isInfuseUsed={isInfuseUsed}
                  setIsInfuseUsed={setIsInfuseUsed}
                  isSenPlayerUsed={isSenPlayerUsed}
                  setIsSenPlayerUsed={setIsSenPlayerUsed}
                  isIinaUsed={isIinaUsed}
                  setIsIinaUsed={setIsIinaUsed}
                />
              </TabPanel>

              <TabPanel value={selectedTab} index={tabSecurity} dir={direction}>
                <SecuritySettings />
              </TabPanel>

              <TabPanel value={selectedTab} index={tabSecurityAudit} dir={direction}>
                {selectedTab === tabSecurityAudit && <SecurityAuditSettings />}
              </TabPanel>

              {gstAvailable && (
                <TabPanel value={selectedTab} index={tabGStreamer} dir={direction}>
                  <GStreamerSettings />
                </TabPanel>
              )}
            </SwipeableViews>
          </>
        ) : (
          <CircularProgress color='secondary' />
        )}
      </Content>

      <FooterSection>
        <Button onClick={handleClose} color='secondary' variant='outlined'>
          {t('Cancel')}
        </Button>

        {selectedTab !== tabSecurity && selectedTab !== tabSecurityAudit && (
          <>
            <Button
              onClick={() => {
                setCacheSize(defaultSettings.CacheSize)
                setCachePercentage(defaultSettings.ReaderReadAHead)
                setPreloadCachePercentage(defaultSettings.PreloadCache)
                setPreloadCacheSize(defaultSettings.PreloadCacheSize)
                updateSettings(defaultSettings)
                // Clear TMDB cache when resetting to defaults
                clearTMDBCache()
              }}
              color='secondary'
              variant='outlined'
            >
              {t('SettingsDialog.ResetToDefault')}
            </Button>

            <Button variant='contained' onClick={handleSave} color='secondary' disabled={!isTrayOpenURLValid}>
              {t('Save')}
            </Button>
          </>
        )}
      </FooterSection>
    </StyledDialog>
  )
}
